import { EquipmentStateName, IEquipment } from '@frontend/equipment/types';
import { ErrorHandler } from '@frontend/error-handler';
import { CommonMessage } from '@frontend/lang';
import { Slot } from '@frontend/slot/types';
import { TransactionType } from '@frontend/transaction/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { useWizardPackages } from '../../hooks/useWizardPackages';
import { useWizardSettings } from '../../hooks/useWizardSettings';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { AvailableSlotOption, SlotOptionsByProduct, chooseBestSingleSlotOption, fetchAvailableSlotOptions } from '../../utils/available-slot';
import { submitLendingReturnTransaction } from '../../utils/submit-lending-return-transaction';
import { toError } from '../../utils/to-error';
import { useKitForProduct } from '../kit-return/shared/use-kit-for-product';
import { KitReturnSlotSelectionGroup, KitReturnStatus } from '../kit-return/shared/use-kit-return';
import { useScreen } from '../useScreen';

const getKitReturnEquipments = (data: Record<string, unknown>): IEquipment[] =>
    Array.isArray(data.kitReturnEquipments) ? (data.kitReturnEquipments as IEquipment[]) : [];

const getStoredProductQuantityLimits = (data: Record<string, unknown>): Map<string, number> => {
    const raw = data.kitReturnProductQuantityLimits;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return new Map();
    return new Map(Object.entries(raw as Record<string, number>));
};

export const useConfirmKitReturnSlot = () => {
    const intl = useIntl();
    const location = useLocation();
    const { store, wizardData, navigationState, onBack, onSubmit } = useScreen();
    const locationEquipment = (location.state as { equipment?: IEquipment } | null)?.equipment ?? null;
    const wizardEquipment = store?.data?.equipment as IEquipment | undefined;
    const scannedEquipments = useMemo(() => getKitReturnEquipments(wizardData as Record<string, unknown>), [wizardData]);
    const stateEquipment = locationEquipment ?? wizardEquipment ?? scannedEquipments[0] ?? null;
    const authenticatedUser = navigationState.user;

    const { entries, isLoading, loadError, retryLoad } = useKitForProduct(stateEquipment?.product_id, stateEquipment);
    const { transactionWorkflowId, dropOffPackageWorkflowId } = useWizardSettings(store);

    const transactionContext = useWizardTransaction({
        store,
        wizardId: store?.wizard.id,
        autoCreate: false,
        autoFetch: false
    });

    const { setPackages, syncPackages } = useWizardPackages({
        store,
        wizardId: store?.wizard.id,
        autoFetch: false,
        transactionContext
    });

    const [isLoadingSlots, setIsLoadingSlots] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [slotSelectionGroups, setSlotSelectionGroups] = useState<KitReturnSlotSelectionGroup[]>([]);
    const [selectedSlotByEquipmentId, setSelectedSlotByEquipmentId] = useState<Map<string, Slot>>(new Map());
    const [error, setError] = useState<string | null>(null);
    const [errorTitle, setErrorTitle] = useState<string | null>(null);
    const isSubmittingRef = useRef<boolean>(false);
    const noSuitableSlotTitle = intl.formatMessage({
        id: 'kiosk.wizard.kit-return-detail.no-suitable-slot-title',
        defaultMessage: 'No suitable SLOT'
    });
    const showError = useCallback((title: string, message: string) => {
        setErrorTitle(title);
        setError(message);
    }, []);
    const resetError = useCallback(() => {
        setError(null);
        setErrorTitle(null);
    }, []);

    const productQuantityLimits = useMemo(() => {
        const limits = new Map(entries.map((entry) => [entry.product.id, entry.quantity]));
        const storedLimits = getStoredProductQuantityLimits(wizardData as Record<string, unknown>);
        for (const [productId, quantity] of storedLimits.entries()) {
            if (!limits.has(productId)) limits.set(productId, quantity);
        }
        return limits;
    }, [entries, wizardData]);
    const scannedQuantityByProductId = useMemo(() => {
        const counts = new Map<string, number>();
        for (const equipment of scannedEquipments) {
            counts.set(equipment.product_id, (counts.get(equipment.product_id) ?? 0) + 1);
        }
        return counts;
    }, [scannedEquipments]);
    const equipmentSlotLookups = useMemo(
        () => [
            ...new Map(
                scannedEquipments.map((equipment) => [
                    `${equipment.product_id}:${equipment.state}`,
                    {
                        productId: equipment.product_id,
                        equipmentState: equipment.state
                    }
                ])
            ).values()
        ],
        [scannedEquipments]
    );
    const kitProductIds = useMemo(() => [...productQuantityLimits.keys()], [productQuantityLimits]);

    const configError = !transactionWorkflowId || !dropOffPackageWorkflowId;

    const status: KitReturnStatus = useMemo(() => {
        if (isLoading) return { kind: 'loading' };
        if (configError) return { kind: 'config-error' };
        if (loadError) return { kind: 'load-error' };
        if (entries.length === 0) return { kind: 'empty' };
        return { kind: 'ready' };
    }, [configError, entries.length, isLoading, loadError]);

    useEffect(() => {
        if (status.kind !== 'ready') return;
        if (!navigationState.iot?.account_id || !navigationState.iot.spot_id || scannedEquipments.length === 0) return;

        let cancelled = false;
        const accountId = navigationState.iot.account_id;
        const spotId = navigationState.iot.spot_id;

        const run = async () => {
            setIsLoadingSlots(true);
            resetError();
            try {
                const slotOptionsByProductAndState = new Map<string, Promise<AvailableSlotOption[]>>();
                const getSlotOptions = (productId: string, equipmentState: EquipmentStateName): Promise<AvailableSlotOption[]> => {
                    const lookupKey = `${productId}:${equipmentState}`;
                    const existing = slotOptionsByProductAndState.get(lookupKey);
                    if (existing) return existing;

                    const promise = fetchAvailableSlotOptions(
                        accountId,
                        spotId,
                        {
                            account_id: accountId,
                            application: store?.functionality?.application,
                            feature: store?.functionality?.feature,
                            functionality_id: store?.functionality?.id,
                            product_id: productId,
                            equipment_state: equipmentState,
                            stock_quantity: scannedQuantityByProductId.get(productId) ?? 1,
                            transaction_type: TransactionType.LENDING
                        },
                        { sortByContent: true, excludedProductId: productId, excludedProductQuantityLimits: productQuantityLimits }
                    );
                    slotOptionsByProductAndState.set(lookupKey, promise);
                    return promise;
                };

                const optionGroups: SlotOptionsByProduct[] = await Promise.all(
                    equipmentSlotLookups.map(async ({ productId, equipmentState }) => ({
                        productId,
                        options: await getSlotOptions(productId, equipmentState)
                    }))
                );
                const bestSlotOption = chooseBestSingleSlotOption(optionGroups, {
                    addedProductQuantities: scannedQuantityByProductId,
                    productQuantityLimits,
                    allowedProductIds: kitProductIds,
                    allowEmptyFallback: true
                });
                if (cancelled) return;

                if (!bestSlotOption) {
                    setSlotSelectionGroups([]);
                    setSelectedSlotByEquipmentId(new Map());
                    showError(
                        noSuitableSlotTitle,
                        intl.formatMessage({
                            id: 'kiosk.wizard.kit-return-detail.no-slot',
                            defaultMessage: 'These items do not fit in an available SLOT. Remove an item or ask an operator for help.'
                        })
                    );
                    return;
                }

                const groups = scannedEquipments.map((equipment) => ({
                    equipment,
                    options: [bestSlotOption],
                    productQuantityLimit: productQuantityLimits.get(equipment.product_id) ?? 1
                }));
                const selectedSlots = new Map(scannedEquipments.map((equipment) => [equipment.id, bestSlotOption.slot]));
                setSlotSelectionGroups(groups);
                setSelectedSlotByEquipmentId(selectedSlots);
            } catch (e: unknown) {
                if (!cancelled) ErrorHandler.handleError({ error: toError(e), entity_type: CommonMessage.OBJECTS.SLOT.DEFAULT });
            } finally {
                if (!cancelled) setIsLoadingSlots(false);
            }
        };

        run();

        return () => {
            cancelled = true;
        };
    }, [
        kitProductIds,
        equipmentSlotLookups,
        intl,
        navigationState.iot?.account_id,
        navigationState.iot?.spot_id,
        noSuitableSlotTitle,
        productQuantityLimits,
        resetError,
        scannedEquipments,
        scannedQuantityByProductId,
        showError,
        status.kind,
        store?.functionality?.application,
        store?.functionality?.feature,
        store?.functionality?.id
    ]);

    const isBusy = isLoading || isLoadingSlots || isSubmitting;
    const submitDisabled =
        status.kind !== 'ready' ||
        isBusy ||
        scannedEquipments.length === 0 ||
        slotSelectionGroups.length === 0 ||
        slotSelectionGroups.some(({ equipment }) => !selectedSlotByEquipmentId.has(equipment.id));

    const handleSubmit = useCallback(async () => {
        if (submitDisabled) return;
        if (isSubmittingRef.current) return;
        isSubmittingRef.current = true;

        const iot = navigationState.iot;
        if (!iot?.account_id || !iot.spot_id) {
            isSubmittingRef.current = false;
            ErrorHandler.handleError({
                error: new Error('Missing IoT context for return transaction'),
                entity_type: CommonMessage.OBJECTS.TRANSACTION.DEFAULT
            });
            return;
        }

        if (!transactionWorkflowId || !dropOffPackageWorkflowId) {
            isSubmittingRef.current = false;
            ErrorHandler.handleError({
                error: new Error('Missing workflow settings for return transaction'),
                entity_type: CommonMessage.OBJECTS.TRANSACTION.DEFAULT
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const lostEquipments = scannedEquipments.filter((equipment) => equipment.state === EquipmentStateName.EQUIPMENT_LOST);
            const returnLaterEquipments = scannedEquipments.filter((equipment) => equipment.state === EquipmentStateName.EQUIPMENT_WAITING_FOR_RETURN);
            const result = await submitLendingReturnTransaction({
                iot: { ...iot, spot_id: iot.spot_id },
                equipments: scannedEquipments,
                userId: authenticatedUser?.id ?? stateEquipment?.user_id ?? scannedEquipments[0]?.user_id ?? null,
                lostEquipments,
                returnLaterEquipments,
                selectedSlotByEquipmentId,
                slotExcludedProductQuantityLimitsByEquipmentId: new Map(scannedEquipments.map((equipment) => [equipment.id, productQuantityLimits])),
                settings: { transactionWorkflowId, dropOffPackageWorkflowId },
                application: store?.functionality?.application,
                feature: store?.functionality?.feature,
                functionality_id: store?.functionality?.id,
                transactionContext,
                setPackages,
                syncPackages
            });

            if (result.status === 'no-slot') {
                showError(
                    noSuitableSlotTitle,
                    intl.formatMessage({
                        id: 'kiosk.wizard.kit-return-detail.no-slot',
                        defaultMessage: 'These items do not fit in an available SLOT. Remove an item or ask an operator for help.'
                    })
                );
                return;
            }

            onSubmit();
        } catch (e: unknown) {
            ErrorHandler.handleError({ error: toError(e), entity_type: CommonMessage.OBJECTS.TRANSACTION.DEFAULT });
        } finally {
            setIsSubmitting(false);
            isSubmittingRef.current = false;
        }
    }, [
        dropOffPackageWorkflowId,
        intl,
        navigationState.iot,
        noSuitableSlotTitle,
        onSubmit,
        productQuantityLimits,
        scannedEquipments,
        selectedSlotByEquipmentId,
        setPackages,
        authenticatedUser?.id,
        stateEquipment?.user_id,
        submitDisabled,
        syncPackages,
        transactionContext,
        transactionWorkflowId,
        showError,
        store?.functionality?.application,
        store?.functionality?.feature,
        store?.functionality?.id
    ]);

    return {
        status,
        entries,
        slotSelectionGroups,
        selectedSlotByEquipmentId,
        isBusy,
        error,
        errorTitle,
        retryLoad,
        resetError,
        submitDisabled,
        onBack,
        onSubmit: handleSubmit
    };
};
